// src/components/Layout.jsx
import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './header.js';
import Footer from './footer.js';

export default function Layout() {
  const location = useLocation();
  const mainRef = useRef(null);

  // Simple scroll restore: jump to hash if present, else scroll to top
  useEffect(() => {
    if (location.hash) {
      requestAnimationFrame(() => {
        const el = document.querySelector(location.hash);
        if (el?.scrollIntoView) el.scrollIntoView({ block: 'start' });
        else window.scrollTo({ top: 0 });
      });
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location.pathname, location.hash]);

  // Move focus to <main> on route change for a11y
  useEffect(() => {
    if (mainRef.current) {
      // Defer focus until after content paints
      const id = requestAnimationFrame(() => {
        mainRef.current.focus({ preventScroll: true });
      });
      return () => cancelAnimationFrame(id);
    }
  }, [location.pathname]);

  return (
    <>
      {/* a11y skip link */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        onFocus={(e) => {
          Object.assign(e.currentTarget.style, {
            left: '8px',
            top: '8px',
            width: 'auto',
            height: 'auto',
            padding: '8px 12px',
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: '6px',
            zIndex: '1000',
          });
        }}
        onBlur={(e) => {
          Object.assign(e.currentTarget.style, {
            left: '-9999px',
            top: 'auto',
            width: '1px',
            height: '1px',
            padding: '0',
            background: 'transparent',
            border: 'none',
          });
        }}
      >
        Skip to main content
      </a>

      <Header />

      <main
        id="main-content"
        ref={mainRef}
        tabIndex={-1}
        role="main"
        // Key by pathname if you want a fresh mount per route; remove the key to preserve state across routes.
        key={`main-${location.pathname}`}
        style={{ minHeight: '50vh' }}
      >
        <Outlet key={`outlet-${location.pathname}`} />
      </main>

      <Footer />
    </>
  );
}
